// PDF generator for the visit prep packet. Mirrors the markdown version
// (buildVisitMarkdown), rendered as a clean printable letterhead-style document
// using pdf-lib. Used for any visit type — primary care, specialist, therapy,
// C&P exam, IME, or other.
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { getTalkingPoints, VISIT_TYPES, isClaimVisit } from "./imeMarkdown";

const PAGE_W = 612;
const PAGE_H = 792;
const M = 54;
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BRAND = rgb(0.11, 0.30, 0.85);
const C_BG_SOFT = rgb(0.95, 0.97, 0.99);
const C_BORDER = rgb(0.85, 0.88, 0.92);

function sanitize(t) {
  return String(t ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function safe(v) {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
}

function wrap(text, maxChars) {
  return String(text || "").split("\n").flatMap((para) => {
    const words = para.split(" ").filter(Boolean);
    const rows = [];
    let row = "";
    words.forEach((w) => {
      if ((row + " " + w).trim().length > maxChars) { rows.push(row); row = w; }
      else row = (row + " " + w).trim();
    });
    if (row) rows.push(row);
    if (rows.length === 0) rows.push("");
    return rows;
  });
}

export async function createImeIntakePdf({
  profile = {},
  master526ez = {},
  issues = [],
  evidence = [],
  appointments = [],
  prescriptions = [],
  statements = [],
  imeIntake = {},
}) {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.TimesRoman);
  const bold = await doc.embedFont(StandardFonts.TimesRomanBold);
  const sansBold = await doc.embedFont(StandardFonts.HelveticaBold);

  let page = doc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - 60;

  const draw = (str, x, yy, opts = {}) => {
    page.drawText(sanitize(str), {
      x, y: yy,
      size: opts.size || 10.5,
      font: opts.font || (opts.bold ? bold : font),
      color: opts.color || C_TEXT,
      maxWidth: opts.maxWidth,
    });
  };

  const newPageIfNeeded = (need) => {
    if (y - need < 60) {
      page = doc.addPage([PAGE_W, PAGE_H]);
      y = PAGE_H - 60;
    }
  };

  const heading = (txt, opts = {}) => {
    newPageIfNeeded(28);
    draw(txt, M, y, { size: opts.size || 14, bold: true, color: opts.color || C_BRAND, font: sansBold });
    y -= (opts.size || 14) + 6;
    page.drawRectangle({ x: M, y, width: PAGE_W - M * 2, height: 1, color: C_BORDER });
    y -= 10;
  };

  const subheading = (txt) => {
    newPageIfNeeded(22);
    draw(txt, M, y, { size: 11.5, bold: true, font: sansBold });
    y -= 16;
  };

  const para = (txt, opts = {}) => {
    const maxChars = opts.maxChars || 92;
    const size = opts.size || 10.5;
    const lineH = size + 3.5;
    wrap(txt, maxChars).forEach((line) => {
      newPageIfNeeded(lineH);
      draw(line, M, y, { size, color: opts.color, font: opts.font, bold: opts.bold });
      y -= lineH;
    });
    y -= 4;
  };

  const blockquote = (txt) => {
    const maxChars = 86;
    const lineH = 12.5;
    const lines = wrap(txt, maxChars);
    const blockH = lines.length * lineH + 10;
    newPageIfNeeded(blockH + 6);
    const yTop = y + 4;
    const yBottom = y - lines.length * lineH;
    page.drawRectangle({ x: M, y: yBottom - 2, width: 3, height: yTop - yBottom + 2, color: C_BRAND });
    lines.forEach((line) => {
      draw(line, M + 10, y, { size: 10, color: C_MUTED });
      y -= lineH;
    });
    y -= 6;
  };

  const kvRow = (k, v, indent = 0) => {
    newPageIfNeeded(15);
    draw(k, M + indent, y, { size: 10, bold: true, font: sansBold });
    draw(safe(v), M + indent + 130, y, { size: 10, maxWidth: PAGE_W - M * 2 - 130 - indent });
    y -= 14;
  };

  const checklistRow = (label, done) => {
    newPageIfNeeded(14);
    const box = done ? "[x]" : "[ ]";
    draw(box, M, y, { size: 10.5, font: sansBold });
    draw(label, M + 22, y, { size: 10.5, color: done ? C_MUTED : C_TEXT, maxWidth: PAGE_W - M * 2 - 22 });
    y -= 14;
  };

  // ============================================================ HEADER
  const veteranName = profile.name || master526ez.veteranName || "Patient";
  const visitTypeDef = VISIT_TYPES.find((v) => v.id === imeIntake.visitType) || VISIT_TYPES[0];
  const claimRelated = isClaimVisit(visitTypeDef);
  const providerName = imeIntake.providerName || imeIntake.examinerName || "";
  const providerSpecialty = imeIntake.providerSpecialty || imeIntake.examinerSpecialty || "";
  const discussionQuestions = imeIntake.discussionQuestions || imeIntake.examinerQuestions || "";

  draw(visitTypeDef.headline.toUpperCase().split(" — ")[0], M, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
  y -= 12;
  const subtitle = visitTypeDef.headline.includes(" — ") ? visitTypeDef.headline.split(" — ")[1] : "Prep Summary";
  draw(subtitle, M, y, { size: 22, bold: true, font: sansBold });
  y -= 28;

  const preparedFor = providerName || (claimRelated ? "Independent Medical Examiner" : "Provider");
  const preparedForLine = `Prepared for: ${preparedFor}` + (providerSpecialty ? `  (${providerSpecialty})` : "");
  draw(preparedForLine, M, y, { size: 10.5, color: C_MUTED });
  y -= 14;
  draw(`Prepared by: ${veteranName}`, M, y, { size: 10.5, color: C_MUTED });
  y -= 14;
  if (imeIntake.visitDate) {
    draw(`Visit date: ${fmtDate(imeIntake.visitDate)}`, M, y, { size: 10.5, color: C_MUTED });
    y -= 14;
  }
  draw(`Date prepared: ${fmtDate(new Date())}`, M, y, { size: 10.5, color: C_MUTED });
  y -= 14;
  draw(`Visit type: ${visitTypeDef.label}`, M, y, { size: 10.5, color: C_MUTED });
  y -= 18;

  blockquote("This document was prepared by the patient using ClaimOps VA. It summarizes their history, current concerns, and questions for this visit. All severity scores and impact descriptions are subjective self-reports. Medical opinions remain the sole judgment of the provider.");

  // ============================================================ 1. PATIENT ID
  heading("1.  Patient identification");
  kvRow("Name", veteranName);
  kvRow("Date of birth", master526ez.dateOfBirth);
  if (claimRelated) {
    kvRow("Branch of service", profile.branch);
    kvRow("Service start", master526ez.serviceStart);
    kvRow("Service end", master526ez.serviceEnd);
    kvRow("Final paygrade", profile.paygrade);
  }
  if (master526ez.email) kvRow("Email", master526ez.email);
  if (master526ez.phone) kvRow("Phone", master526ez.phone);
  y -= 6;

  // ============================================================ 1.5 REASON FOR VISIT
  if (imeIntake.visitReason) {
    heading("Reason for this visit");
    imeIntake.visitReason.split("\n").map((p) => p.trim()).filter(Boolean).forEach((p) => para(p));
  }

  // ============================================================ 2. CONDITIONS
  heading(claimRelated ? "2.  Conditions under examination" : "2.  Active conditions / ongoing concerns");
  if (issues.length === 0) {
    para("No conditions specified.", { color: C_MUTED });
  } else {
    issues.forEach((iss, i) => {
      subheading(`2.${i + 1}.  ${iss.claimedAs || "(untitled)"}  -  DC ${iss.code || "?"}`);
      kvRow("Theory", iss.theory);
      if (iss.secondaryTo) kvRow("Secondary to", iss.secondaryTo);
      if (iss.secondaryReason) {
        draw("Secondary rationale:", M, y, { size: 10, bold: true, font: sansBold });
        y -= 14;
        para(iss.secondaryReason, { color: C_MUTED });
      }
      if (iss.bilateralSide) kvRow("Side", iss.bilateralSide);
      if (iss.onset) {
        draw("Onset / first noticed:", M, y, { size: 10, bold: true, font: sansBold });
        y -= 14;
        para(iss.onset, { color: C_MUTED });
      }
      if (iss.functionalImpact) {
        draw("Functional impact (patient's report):", M, y, { size: 10, bold: true, font: sansBold });
        y -= 14;
        blockquote(iss.functionalImpact);
      }
      const linked = statements.filter((s) => s.conditionId === iss.id || s.conditionName === iss.claimedAs);
      if (linked.length > 0) {
        kvRow("Statements attached", linked.map((s) => `${s.type || "personal"} (${fmtDate(s.createdAt)})`).join(", "));
      }
      y -= 4;
    });
  }

  // ============================================================ 3. SERVICE HISTORY (claim only)
  if (claimRelated && imeIntake.serviceHistory) {
    heading("3.  Service history (patient narrative)");
    imeIntake.serviceHistory.split("\n").map((p) => p.trim()).filter(Boolean).forEach((p) => para(p));
  }

  // ============================================================ 4. TIMELINE
  heading(claimRelated ? "4.  Chronological timeline" : "4.  Recent timeline");
  const tl = [];
  if (claimRelated && master526ez.serviceStart) tl.push({ date: master526ez.serviceStart, event: `Service start (${profile.branch || "—"})`, source: "DD-214 / profile" });
  if (claimRelated && master526ez.serviceEnd) tl.push({ date: master526ez.serviceEnd, event: "Service end / separation", source: "DD-214 / profile" });
  (imeIntake.timeline || []).forEach((e) => tl.push({ date: e.date, event: e.event, source: e.source || "Patient note" }));
  appointments.filter((a) => a.completed).forEach((a) => {
    tl.push({
      date: (a.datetime || "").slice(0, 10),
      event: `${a.type === "cp_exam" ? "C&P Exam" : "Medical visit"}: ${a.title || "Appointment"}${a.provider ? ` (${a.provider})` : ""}`,
      source: "Appointment record",
    });
  });
  tl.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  if (tl.length === 0) {
    para("No timeline events recorded.", { color: C_MUTED });
  } else {
    // Header row
    newPageIfNeeded(18);
    page.drawRectangle({ x: M, y: y - 4, width: PAGE_W - M * 2, height: 16, color: C_BG_SOFT });
    draw("DATE", M + 4, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("EVENT", M + 90, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("SOURCE", M + 360, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    y -= 18;
    tl.forEach((e) => {
      const evtLines = wrap(e.event, 50);
      const srcLines = wrap(e.source, 28);
      const rowH = Math.max(evtLines.length, srcLines.length, 1) * 12 + 4;
      newPageIfNeeded(rowH + 2);
      draw(fmtDate(e.date), M + 4, y, { size: 9.5 });
      evtLines.forEach((ln, i) => draw(ln, M + 90, y - i * 12, { size: 9.5 }));
      srcLines.forEach((ln, i) => draw(ln, M + 360, y - i * 12, { size: 9, color: C_MUTED }));
      y -= rowH;
      page.drawRectangle({ x: M, y: y + 1, width: PAGE_W - M * 2, height: 0.5, color: C_BORDER });
    });
    y -= 6;
  }

  // ============================================================ 5. DOCUMENT MANIFEST
  heading("5.  Documents provided (appendix manifest)");
  const docs = [
    ...evidence.map((ev) => ({
      title: ev.title || ev.label || "Untitled evidence",
      date: ev.date || ev.dateOfRecord || "",
      source: ev.source || ev.type || "Veteran-supplied",
      summary: ev.summary || "",
    })),
    ...(imeIntake.documents || []),
  ];
  if (docs.length === 0) {
    para("No documents recorded.", { color: C_MUTED });
  } else {
    newPageIfNeeded(18);
    page.drawRectangle({ x: M, y: y - 4, width: PAGE_W - M * 2, height: 16, color: C_BG_SOFT });
    draw("#", M + 4, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("DOCUMENT", M + 24, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("DATE", M + 240, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("SOURCE", M + 310, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("SUMMARY", M + 410, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    y -= 18;
    docs.forEach((d, i) => {
      const titleLines = wrap(d.title, 36);
      const srcLines = wrap(d.source || "", 18);
      const sumLines = wrap(d.summary || "", 22);
      const rowH = Math.max(titleLines.length, srcLines.length, sumLines.length, 1) * 11.5 + 4;
      newPageIfNeeded(rowH + 2);
      draw(String(i + 1), M + 4, y, { size: 9.5 });
      titleLines.forEach((ln, k) => draw(ln, M + 24, y - k * 11.5, { size: 9.5 }));
      draw(fmtDate(d.date), M + 240, y, { size: 9 });
      srcLines.forEach((ln, k) => draw(ln, M + 310, y - k * 11.5, { size: 9, color: C_MUTED }));
      sumLines.forEach((ln, k) => draw(ln, M + 410, y - k * 11.5, { size: 9, color: C_MUTED }));
      y -= rowH;
      page.drawRectangle({ x: M, y: y + 1, width: PAGE_W - M * 2, height: 0.5, color: C_BORDER });
    });
    y -= 6;
  }

  // ============================================================ 6. TALKING POINTS / NEXUS
  heading(claimRelated ? "6.  Nexus checklists (per condition)" : "6.  Talking points (per condition)");
  if (issues.length === 0) {
    para("No conditions to check against.", { color: C_MUTED });
  } else {
    issues.forEach((iss, i) => {
      subheading(`6.${i + 1}.  ${iss.claimedAs}`);
      const checks = getTalkingPoints(iss, imeIntake.checklists?.[iss.id] || {}, visitTypeDef);
      checks.forEach((c) => checklistRow(c.label, c.done));
      y -= 6;
    });
  }

  // ============================================================ 7. QUESTIONS
  heading(claimRelated ? "7.  Questions for the examiner" : "7.  Topics / questions to cover");
  const qs = discussionQuestions.split("\n").map((q) => q.trim()).filter(Boolean);
  if (qs.length === 0) {
    para("No pre-written questions.", { color: C_MUTED });
  } else {
    qs.forEach((q) => {
      newPageIfNeeded(20);
      draw("*", M, y, { size: 12, bold: true, color: C_BRAND, font: sansBold });
      const lines = wrap(q, 86);
      lines.forEach((ln, idx) => {
        if (idx > 0) newPageIfNeeded(13);
        draw(ln, M + 14, y, { size: 10.5 });
        y -= 13;
      });
      y -= 4;
    });
  }

  // ============================================================ 8. CURRENT MEDS
  const activeRx = prescriptions.filter((rx) => !rx.archived);
  if (activeRx.length > 0) {
    heading("8.  Current medications");
    newPageIfNeeded(18);
    page.drawRectangle({ x: M, y: y - 4, width: PAGE_W - M * 2, height: 16, color: C_BG_SOFT });
    draw("MEDICATION", M + 4, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("DOSAGE", M + 180, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("PRESCRIBER", M + 250, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("LAST FILL", M + 360, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("DAYS", M + 430, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    draw("REFILLS", M + 470, y, { size: 9, bold: true, font: sansBold, color: C_BRAND });
    y -= 18;
    activeRx.forEach((rx) => {
      newPageIfNeeded(14);
      draw(rx.medName || "—", M + 4, y, { size: 9.5, maxWidth: 170 });
      draw(safe(rx.dosage), M + 180, y, { size: 9.5 });
      draw(safe(rx.prescriber), M + 250, y, { size: 9, color: C_MUTED });
      draw(fmtDate(rx.lastFilled), M + 360, y, { size: 9 });
      draw(safe(rx.daysSupply), M + 430, y, { size: 9 });
      draw(safe(rx.refillsRemaining), M + 470, y, { size: 9 });
      y -= 13;
    });
  }

  // ============================================================ FOOTER
  const pages = doc.getPages();
  pages.forEach((p, idx) => {
    p.drawText(sanitize(`Generated by ClaimOps VA  -  ${fmtDate(new Date())}`), {
      x: M, y: 34, size: 8, font, color: C_MUTED,
    });
    p.drawText(sanitize(`Page ${idx + 1} of ${pages.length}`), {
      x: PAGE_W - M - 70, y: 34, size: 8, font, color: C_MUTED,
    });
  });

  return await doc.save();
}
